import { displayPercentageDiscount } from "@website/accommodation-ui/helpers/calculatePercentageDiscount";
import SnappPayLogo from "@website/accommodation-ui/hotel-reserve-booking-card/assets/snapp_pay_logo.png";
import { HotelReserveBookingCardType } from "@website/accommodation-ui/hotel-reserve-booking-card/hotel-reserve-booking-card.type";
import { useJwtToken } from "@website/hooks/auth-manager";
import { MaterialIcon } from "@website/icons/material";
import { Button } from "@website/ui/button";
import { CounterButton } from "@website/ui/counter-button";
import { Datepicker } from "@website/ui/datepicker";
import { InfoChips } from "@website/ui/info-chips";
import { LazyImage } from "@website/ui/lazy-image";
import { MiniChips } from "@website/ui/mini-chips";
import { deepCompare } from "@website/utils/deep-compare";
import { numberConvert } from "@website/utils/number-convert";
import { numberSeparator } from "@website/utils/number-separator";
import { useLoginModal } from "@website/widget/login";
import { useRouter } from "next/router";
import { FC, memo, useCallback } from "react";

const Component: FC<HotelReserveBookingCardType> = (properties) => {
  const { phoneNumber } = useJwtToken();
  const {
    isLoading,
    extraCapacity = 0,
    nightCount,
    capacity = 0,
    passengersCount = 0,
    price,
    minStay,
    priceOfExtraPassengers,
    discount,
    total_price,
    total_price_off,
    calendarDate,
    increaseDisabled,
    decreaseDisabled,
    submitDisabled,
    submitDisabledMessage,
    reviewRate,
    disableLimit,
    onChangeDatePicker,
    createBook,
    onChangePassengersCount,
    showMinStayAlarm,
    calendarData,
    disableDates,
    basePrice,
    snappPayData,
    cancellationRuleHintText,
    datePickerIsOpenAtInit
  } = properties;
  const { onLoginModalOpen } = useLoginModal();
  const router = useRouter();
  const isVilla = process.env.NEXT_PUBLIC_APP_NAME === "villa";
  const openDatePicker = useCallback(() => {
    const search_date_range = document.querySelector<HTMLDivElement>(
      `[data-testid="search_date_range"]`
    );
    search_date_range?.scrollIntoView({ block: "center" });
    search_date_range?.click();
    setTimeout(
      () =>
        document
          .querySelector<HTMLInputElement>(
            `[data-testid="date_picker_start_input"]`
          )
          ?.click(),
      200
    );
  }, []);

  const submitOnClick = useCallback(() => {
    if (!router?.query?.["date_from"] || !router?.query?.["date_to"]) {
      openDatePicker();
    }

    if (
      !phoneNumber &&
      router?.query?.["date_from"] &&
      router?.query?.["date_to"]
    )
      return onLoginModalOpen().then(() => createBook?.());
    if (router?.query?.["date_from"] && router?.query?.["date_to"])
      createBook?.();
  }, [createBook, phoneNumber, router]);

  return (
    <div className="flex h-full flex-col justify-between px-6 pt-6">
      <div>
        {!submitDisabled && (
          <div className="mb-6 flex w-full flex-row items-center justify-between">
            {/*<div className="text-caption text-on-surface-high-emphasis">*/}
            {/*  {"قیمت از شبی"}*/}
            {/*</div>*/}
            {/*<div className="text-on-surface-high-emphasis text-subtitle-1">*/}
            {/*  {i18n("global.price_with_tooman_currency", {*/}
            {/*    price: numberConvert(numberSeparator(pricePerNight), {*/}
            {/*      locale: router.locale*/}
            {/*    })*/}
            {/*  })}*/}
            {/*</div>*/}
            {reviewRate ? (
              <MiniChips
                StartIcon={<MaterialIcon name="star" />}
                color="success"
                text={reviewRate}
                variant="accent"
              />
            ) : null}
          </div>
        )}
        <Datepicker
          acceptEmpty
          calendarData={calendarData}
          disableDates={disableDates}
          disableLimit={disableLimit}
          error={calendarDate?.length < 2}
          fromTitle="تاریخ ورود"
          fullWidth
          helperText={
            calendarDate?.length < 2
              ? "لطفا تاریخ ورود و خروج خود را انتخاب نمایید."
              : ""
          }
          mode="range"
          onChange={onChangeDatePicker}
          toTitle="تاریخ خروج"
          value={calendarDate}
          initialOpen={datePickerIsOpenAtInit}
        />
        <div className="mt-4 flex items-center justify-between">
          <div className="flex flex-col">
            <div className="text-subtitle-1 text-on-surface-high-emphasis">
              تعداد مسافر
            </div>
            <span className="text-body-2 text-on-surface-medium-emphasis mt-1 lg:hidden">
              {Number(router?.query?.room_capacity || 0) > 0 &&
                `${numberConvert(
                  Number(router?.query?.room_capacity) -
                    Number(router?.query?.extra_capacity || 0),
                  { locale: router.locale }
                )} نفر پایه`}
              {Number(router?.query?.extra_capacity || 0) > 0 &&
                ` + ${numberConvert(router?.query?.extra_capacity, {
                  locale: router.locale
                })} نفر اضافه`}
            </span>
          </div>
          <CounterButton
            color="secondary"
            decreaseDisabled={decreaseDisabled}
            increaseDisabled={increaseDisabled}
            locale={router.locale}
            onChange={onChangePassengersCount}
            value={passengersCount}
            variant="outline"
          />
        </div>
        {Number(router.query["room_capacity"] || 0) === 0 && (
          <p className="text-error text-caption">
            تعداد مسافران را انتخاب کنید.
          </p>
        )}
      </div>

      <div>
        {!submitDisabled && (
          <>
            <div className="my-6">
              {router?.query?.["date_from"] &&
                router?.query?.["date_to"] &&
                passengersCount > 0 && (
                  <div className="mb-4 flex items-center justify-between">
                    <div className="text-body-2 text-on-surface-medium-emphasis">
                      {!submitDisabledMessage &&
                        `${numberConvert(nightCount, {
                          locale: router.locale
                        })} شب برای ${numberConvert(
                          passengersCount > capacity
                            ? capacity
                            : passengersCount,
                          { locale: router.locale }
                        )} نفر`}
                    </div>
                    <span className="border-outline-thin mx-1 flex-1 border-t border-dashed" />

                    <div className="text-on-surface-medium-emphasis text-body-2">
                      {`${numberConvert(
                        numberSeparator(isVilla ? total_price : price),
                        {
                          locale: router.locale
                        }
                      )} تومان`}
                    </div>
                  </div>
                )}
              {(!router?.query?.["date_from"] ||
                !router?.query?.["date_to"] ||
                Number(router.query["room_capacity"] || 0) <= 0) && (
                <div className="flex items-center justify-between">
                  <div className="text-subtitle-2 text-on-surface-high-emphasis">
                    قیمت از شبی
                  </div>
                  <span className="border-outline-thin mx-1 flex-1 border-t border-dashed" />

                  <div className="text-on-surface-high-emphasis text-body-2">
                    {`${numberConvert(numberSeparator(basePrice), {
                      locale: router.locale
                    })} تومان`}
                  </div>
                </div>
              )}
              {extraCapacity ? (
                <div className="mb-4 flex items-center justify-between">
                  <div className="text-body-2 text-on-surface-medium-emphasis">{`${numberConvert(
                    extraCapacity,
                    { locale: router.locale }
                  )} نفر اضافه`}</div>
                  <span className="border-outline-thin mx-1 flex-1 border-t border-dashed" />

                  <div className="text-on-surface-medium-emphasis text-body-2">
                    {priceOfExtraPassengers !== 0 &&
                      `${numberConvert(
                        numberSeparator(priceOfExtraPassengers),
                        { locale: router.locale }
                      )} تومان`}
                  </div>
                </div>
              ) : null}

              {discount ? (
                <div className="mb-4 flex items-center justify-between">
                  <div
                    className={`text-body-2 ${
                      isVilla
                        ? "!text-success"
                        : "text-on-surface-medium-emphasis"
                    }`}
                  >
                    تخفیف
                  </div>
                  <span className="border-outline-thin mx-1 flex-1 border-t border-dashed" />

                  <div
                    className={`${
                      isVilla
                        ? "!text-success"
                        : "text-on-surface-medium-emphasis"
                    } text-body-2`}
                  >
                    {`${numberConvert(numberSeparator(discount), {
                      locale: router.locale
                    })} تومان`}
                  </div>
                </div>
              ) : null}
            </div>
            {router?.query?.["date_from"] &&
              router?.query?.["date_to"] &&
              Number(router.query["room_capacity"] || 0) > 0 && (
                <>
                  <div className="bg-outline-thin h-px w-full" />
                  {cancellationRuleHintText && (
                    <p className="text-primary-inverse flex pt-4 text-xs">
                      <MaterialIcon name="info" variant="outlined" />
                      <span className="pr-2">{cancellationRuleHintText}</span>
                    </p>
                  )}
                  <div className="mt-6 flex items-center justify-between">
                    <div className="text-subtitle-2 text-on-surface-high-emphasis">
                      جمع مبلغ قابل پرداخت
                    </div>
                    <span className="border-outline-thin mx-1 flex-1 border-t border-dashed sm:hidden" />
                    <div className="text-on-surface-high-emphasis text-headline-6">
                      {isVilla && (
                        <div className="flex w-full items-center justify-end gap-2">
                          {total_price !== total_price_off && (
                            <>
                              <span className="text-on-surface-disable text-body-2 line-through">
                                {numberConvert(numberSeparator(total_price), {
                                  locale: router.locale || "fa"
                                })}
                              </span>
                              <InfoChips
                                className="!w-[30px] justify-center !p-[2px]"
                                size="medium"
                                text={displayPercentageDiscount(
                                  total_price as number,
                                  total_price_off as number
                                )}
                                variant="round-accent"
                              />
                            </>
                          )}
                        </div>
                      )}
                      {`${numberConvert(numberSeparator(total_price_off), {
                        locale: router.locale
                      })} تومان`}
                    </div>
                  </div>
                </>
              )}
          </>
        )}
        <div
          className={`flex flex-col gap-2 ${
            router?.query?.["date_from"] && router?.query?.["date_to"]
              ? "py-6"
              : "pb-6 pt-0"
          }`}
        >
          <Button
            disabled={Boolean(
              (!isLoading && submitDisabled && calendarDate?.length === 2) ||
                (Number(router.query["room_capacity"] || 0) === 0 &&
                  router?.query?.["date_from"] &&
                  router?.query?.["date_to"]) ||
                (Boolean(minStay) && Number(nightCount) < Number(minStay))
            )}
            fullWidth
            loading={isLoading}
            onClick={submitOnClick}
            size="large"
          >
            {" "}
            {router?.query?.["date_from"] && router?.query?.["date_to"]
              ? "درخواست رزرو"
              : "انتخاب تاریخ"}
          </Button>
          {showMinStayAlarm &&
          minStay &&
          Number(nightCount) < Number(minStay) ? (
            <div className="text-error text-caption pt-4">{`حداقل ${numberConvert(
              minStay,
              { locale: router.locale }
            )} شب اقامت`}</div>
          ) : null}
          {submitDisabledMessage && !isLoading && (
            <p className="text-error text-caption">{submitDisabledMessage}</p>
          )}
          {snappPayData?.isEligible && snappPayData?.showDownPayment && (
            <>
              <div className="hidden flex-row items-center gap-1 self-center px-6 pb-2 pt-6 lg:flex">
                <LazyImage src={SnappPayLogo?.src} width={20} height={20} />
                <span className="text-body-2 text-on-surface-medium-emphasis">
                  امکان پرداخت قسطی با اسنپ‌‌پی
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
export const HotelReserveBookingCardDetail = memo(Component, deepCompare);
